/**
 * Block registry for the modular Lexicanon meeting-summary page.
 *
 * Each summary "block" is one card on the meeting-detail page. This registry is
 * the single source of truth for which blocks exist and their default column,
 * order, and visibility — consumed by the renderer (frontend) and, from Phase B,
 * by the per-company curate config + its validation (backend).
 *
 * v1 (Level 1) is curate-only: a company can show/hide, reorder, and re-column
 * blocks; mobile auto-stacks. The recording Player is `required` chrome — the
 * seek/timestamp pills depend on its player context, so it can't be toggled off.
 * Lexicanon-only for now; the shape leaves room for Voxcanon blocks later.
 *
 * See tasks/modular-summary-blocks-plan.md.
 */

/**
 * Which column a block lives in.
 *
 * `full` is kept for stored layouts and for the required player. The meeting
 * page does not render a band above the summary: the player sits in the side
 * rail (wide) or the bottom dock (narrow). The curate list only edits `main`
 * and `side`, so the player's pinned `full` column keeps it out of that list.
 */
export type TSummaryColumn = "full" | "main" | "side";

/** Stable block ids — also the discriminant the renderer maps to a card. */
export type TSummaryBlockType =
	| "player"
	| "summary"
	| "decisions"
	| "action_items"
	| "open_questions"
	| "topics"
	| "attendees"
	| "quotes"
	| "sentiment"
	| "coach_questions"
	| "follow_up"
	| "quality"
	| "talk_time"
	| "cost"
	| "source"
	| "suggested_jargon"
	| "visual_report";

export interface ISummaryBlockDef {
	type: TSummaryBlockType;
	/** Human label for the curate UI (translated in the UI; this is the fallback). */
	displayName: string;
	defaultColumn: TSummaryColumn;
	/** Default order within its column, ascending. */
	defaultOrder: number;
	defaultVisible: boolean;
	/** Required blocks cannot be toggled off (e.g. the recording Player). */
	required: boolean;
}

/** A block's effective placement, after resolving company config over defaults. */
export interface IResolvedSummaryBlock {
	type: TSummaryBlockType;
	column: TSummaryColumn;
	order: number;
	visible: boolean;
}

/**
 * The Lexicanon block catalog. The player stays required chrome (column `full`,
 * rendered in the rail or dock). The visual report follows the main column so
 * the summary, decisions and action items come first.
 */
export const LEXICANON_SUMMARY_BLOCKS: readonly ISummaryBlockDef[] = [
	{
		type: "player",
		displayName: "Recording",
		defaultColumn: "full",
		defaultOrder: 0,
		defaultVisible: true,
		required: true,
	},
	// Offer + viewer. End of the main column (after quotes, order 6) so the
	// result is what you see first. Self-hides when the meeting has nothing
	// to offer and nothing generated.
	{
		type: "visual_report",
		displayName: "Visual report",
		defaultColumn: "main",
		defaultOrder: 8,
		defaultVisible: true,
		required: false,
	},
	// main column
	{
		type: "summary",
		displayName: "Summary",
		defaultColumn: "main",
		defaultOrder: 0,
		defaultVisible: true,
		required: false,
	},
	{
		type: "decisions",
		displayName: "Decisions",
		defaultColumn: "main",
		defaultOrder: 1,
		defaultVisible: true,
		required: false,
	},
	{
		type: "action_items",
		displayName: "Action items",
		defaultColumn: "main",
		defaultOrder: 2,
		defaultVisible: true,
		required: false,
	},
	{
		type: "open_questions",
		displayName: "Open questions",
		defaultColumn: "main",
		defaultOrder: 3,
		defaultVisible: true,
		required: false,
	},
	{
		type: "topics",
		displayName: "Topics",
		defaultColumn: "main",
		defaultOrder: 4,
		defaultVisible: true,
		required: false,
	},
	{
		type: "attendees",
		displayName: "Attendees",
		defaultColumn: "main",
		defaultOrder: 5,
		defaultVisible: true,
		required: false,
	},
	{
		type: "quotes",
		displayName: "Quotes",
		defaultColumn: "main",
		defaultOrder: 6,
		defaultVisible: true,
		required: false,
	},
	// side column
	{
		type: "sentiment",
		displayName: "Sentiment",
		defaultColumn: "side",
		defaultOrder: 0,
		defaultVisible: true,
		required: false,
	},
	{
		type: "coach_questions",
		displayName: "Coaching questions",
		defaultColumn: "side",
		defaultOrder: 1,
		defaultVisible: true,
		required: false,
	},
	{
		type: "follow_up",
		displayName: "Follow-up",
		defaultColumn: "side",
		defaultOrder: 2,
		defaultVisible: true,
		required: false,
	},
	{
		type: "quality",
		displayName: "Quality",
		defaultColumn: "side",
		defaultOrder: 3,
		defaultVisible: true,
		required: false,
	},
	{
		type: "talk_time",
		displayName: "Talk-share",
		defaultColumn: "side",
		defaultOrder: 4,
		defaultVisible: true,
		required: false,
	},
	{
		type: "cost",
		displayName: "Cost split",
		defaultColumn: "side",
		defaultOrder: 5,
		defaultVisible: true,
		required: false,
	},
	{
		type: "source",
		displayName: "Source",
		defaultColumn: "side",
		defaultOrder: 6,
		defaultVisible: true,
		required: false,
	},
	{
		type: "suggested_jargon",
		displayName: "Suggested vocabulary",
		defaultColumn: "side",
		defaultOrder: 7,
		defaultVisible: true,
		required: false,
	},
];

/** A single block's overrides within a company's stored layout config. */
export interface ISummaryBlockOverride {
	type: TSummaryBlockType;
	visible?: boolean;
	order?: number;
	column?: TSummaryColumn;
}

/** A company's stored summary-layout config (the `summary_layout` setting). */
export interface ISummaryLayoutConfig {
	/** Schema version for forward migration. */
	version: number;
	blocks: ISummaryBlockOverride[];
}

/** Current config schema version. */
export const SUMMARY_LAYOUT_VERSION = 1;

/**
 * Resolve the effective per-block layout from the registry defaults + an
 * optional per-company config.
 *
 * Merge rules:
 *  - The registry is the source of truth: every catalog block appears in the
 *    result, so a block ADDED later shows up for existing companies (whose
 *    config simply has no entry → defaults).
 *  - A config entry overrides that block's `visible`/`order`/`column`.
 *  - Config entries for types NOT in the registry are ignored (forward-compat
 *    when a block is renamed/removed).
 *  - `required` blocks (e.g. the Player) are pinned: always visible and in their
 *    default column regardless of config — enforced here so a bad/hostile config
 *    can't hide them.
 *  - A stored `full` column is the retired top band. For every block except the
 *    pinned player it is dropped, and that block takes its current default
 *    column and order (the visual report lands at the end of the main column).
 *  - Any other column value that isn't `main` or `side` is unknown and falls
 *    back to the block's default column. A numeric order is kept.
 *
 * No config → the catalog defaults.
 */
function isContentColumn(column: unknown): column is "main" | "side" {
	return column === "main" || column === "side";
}

export function resolveSummaryLayout(config?: ISummaryLayoutConfig | null): IResolvedSummaryBlock[] {
	const overrides = new Map<TSummaryBlockType, ISummaryBlockOverride>((config?.blocks ?? []).map((o) => [o.type, o]));
	return LEXICANON_SUMMARY_BLOCKS.map((b) => {
		const o = overrides.get(b.type);
		const storedColumn: unknown = o?.column;
		// Old saves put the report (and anything else) in the top band.
		// That order belonged to a column the page no longer renders.
		const retiredBand = storedColumn === "full";
		const column = b.required ? b.defaultColumn : isContentColumn(storedColumn) ? storedColumn : b.defaultColumn;
		const order = retiredBand && !b.required ? b.defaultOrder : (o?.order ?? b.defaultOrder);
		return {
			type: b.type,
			column,
			order,
			visible: b.required ? true : (o?.visible ?? b.defaultVisible),
		};
	});
}
